import React, { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  EditContainer,
  PageContainer,
  PageContent,
} from '../component/Containers/Containers';
import {
  CardBody,
  CardContainer,
  CardInput,
  CardText,
  FormButton,
  FormRow,
  GhostButton,
  Toggler,
} from '../component/Card/CardComponents';
import { addNewStation, filterByState } from '../store/features/stationSlice';
import { useDispatch, useSelector } from 'react-redux';
import useForceUpdate from '../hooks/useForceUpdate';
import { setIsLoadingTo } from '../store/features/adminSlice';
import { HttpListClient } from '../utils/HttpClient';
import routes from '../router/routes';

const styles = {
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: state.isSelected ? '#323637' : '#323637',
    backgroundColor: state.isSelected ? '#E6E8E8' : '#F2F3F3',
  }),
  container: (provided, state) => ({
    ...provided,
    height: '42px',
    marginTop: '20px',
    width: '270px',
  }),
  menu: (provided, state) => ({
    ...provided,
    margin: 0,
    border: 0,
    outline: 0,
    backgroundColor: '#F2F3F3',
    borderRadius: '0 0 5px 5px',
    fontSize: '18px',
    fontFamily: 'inherit',
    color: '#323637',
    zIndex: '20',
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#E9ECEF',
    border: 0,
    outline: 0,
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#323637',
    height: '100%',
    borderRadius: '10px',
    width: '274px',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#323637',
    backgroundColor: '#E9ECEF',
    cursor: 'pointer',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: '#323637',
    fontFamily: 'Montserrat',
    fontSize: '14px',
  }),
};

/**
 * @component
 * returns the Add component which allows to create a new station saving it in the DB.
 * @returns {void}
 */
const Add = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const forceUpdate = useForceUpdate();

  const [currentName, setCurrentName] = useState('');
  const [currentProvince, setCurrentProvince] = useState('');
  const [currentSource, setCurrentSource] = useState('');
  const [currentActive, setCurrentActive] = useState(true);
  const [options, setOptions] = useState({});

  const nameRef = useRef(null);
  const sourceRef = useRef(null);

  const { searchByState } = useSelector((state) => state.stations);
  const SAVE_STATION_URL = 'saveStation';
  const GET_PROVINCES_URL = '/getProvinces';

  const { isLoading, user } = useSelector((state) => state.admin);
  const { allStations } = useSelector((state) => state.stations);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    dispatch(setIsLoadingTo(true));
    let map = [];
    let fetchedProvinces = await HttpListClient.get(GET_PROVINCES_URL);
    fetchedProvinces.data.forEach((province) => {
      let newProvince = { value: province.Id, label: province.Name };
      map.push(newProvince);
    });
    setOptions(map);
    forceUpdate();
    dispatch(filterByState(searchByState));
    dispatch(setIsLoadingTo(false));
  }, [location, history]);

  /**
   *  returns a string className using a condition which checks the current active status of a station.
   * @param {boolean} state
   * @returns {string}
   */
  const isClassActive = (state) => (state ? 'active' : '');

  /**
   * saves the station in the DB and displays a toast depending if and error happened or not.
   * @returns {void}
   */
  const handleSubmit = async () => {
    let duplicate = false;
    let hasError = false;
    let validNameLength = nameRef.current.value.length > 4;
    let validSourceURI = sourceRef.current.value.length > 4;
    let edittedStation = {
      Name: currentName,
      PlaceId: currentProvince,
      IsActive: currentActive,
      Source: currentSource,
      IsPlaying: false,
      LastListened: null,
      Favorite: false,
      EntryBy: parseInt(user.id) || parseInt(user.Id)
    };
    try {
      dispatch(setIsLoadingTo(true));
      allStations.forEach((station) => {
        if (station.Name.trim() == currentName.trim()) {
          duplicate = true;
        }
      });

      if (duplicate == true) {
        Swal.fire({
          title: 'Error',
          text: 'No se admiten emisoras con nombres repetidos.',
          icon: 'error',
          confirmButtonText: 'Ok',
          confirmButtonColor: '#003876',
        });
      }

      if (validNameLength && validSourceURI && !duplicate) {
        await HttpListClient.post(SAVE_STATION_URL, edittedStation);
        console.log(edittedStation)
      } else {
        hasError = true;
        throw new Error('Invalid name and/or Source');
      }
    } catch (error) {
      hasError = true;
      if (!duplicate) {
        Swal.fire({
          title: 'Error',
          icon: 'error',
          timer: 2000,
          text: 'Ha ocurrido un error por favor intentar mas tarde.',
          confirmButtonColor: '#003876',
        });
      }
    } finally {
      if (!duplicate) {
        if (hasError) {
          Swal.fire({
            title: 'Error',
            text: 'Se ha producido un error al intentar agregar la emisora',
            icon: 'error',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#003876',
          });
        } else {
          Swal.fire({
            title: 'Exito',
            text: 'Se ha agregado la emisora exitosamente',
            icon: 'success',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#003876',
          });
          dispatch(addNewStation({ ...edittedStation, Id: uuidv4() }));
          history.push(routes.admin);
        }
        dispatch(filterByState(searchByState));
      }
      dispatch(setIsLoadingTo(false));
    }
  };

  /**
   * when called it will take the user to previous page.
   * @returns {void}
   */
  const handleCancel = () => history.push('gestion-administrador');

  return (
    <PageContainer>
      <PageContent>
        <EditContainer style={{ marginRight: '30px' }}>
          <div
            style={{
              width: '90%',
              height: '100%',
              alignSelf: 'center',
              justifySelf: 'center',
              display: 'flex',
              marginTop: '15px',
              justifyContent: 'center',
            }}>
            {isLoading ? null : (
              <CardContainer style={{ backgroundColor: '#F6F7F9' }}>
                <CardBody style={{ margin: 'auto' }}>
                  <label style={{ marginBottom: '-10px', marginTop: '20px' }}>
                    Nombre de emisora
                  </label>
                  <CardInput
                    maxLength="25"
                    onChange={(e) => setCurrentName(e.target.value)}
                    value={currentName}
                    ref={nameRef}
                  />
                  <label style={{ marginBottom: '-10px', marginTop: '20px' }}>
                    Provincia
                  </label>
                  <Select
                    options={options}
                    styles={styles}
                    label="Filtrar"
                    defaultValue={{ value: 1, label: 'Provincias' }}
                    onChange={(e) => setCurrentProvince(e.value)}
                  />
                  <label style={{ marginBottom: '-10px', marginTop: '20px' }}>
                    URL de transmision
                  </label>
                  <CardInput
                    ref={sourceRef}
                    onChange={(e) => setCurrentSource(e.target.value)}
                    value={currentSource}
                  />
                  <FormRow>
                    <CardText
                      style={{
                        fontSize: '1.7rem',
                        margin: '0',
                        marginRight: 'auto',
                        fontWeight: '500',
                      }}>
                      Estatus
                    </CardText>
                    <Toggler
                      onClick={() => setCurrentActive(!currentActive)}
                      className={isClassActive(currentActive)}
                      isActive={true}
                    />
                  </FormRow>
                  <FormRow>
                    <GhostButton onClick={handleCancel}>Cancelar</GhostButton>
                    <FormButton onClick={handleSubmit}>Guardar</FormButton>
                  </FormRow>
                </CardBody>
              </CardContainer>
            )}
          </div>
        </EditContainer>
      </PageContent>
    </PageContainer>
  );
};

export default Add;
